import type { User } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useState } from 'react'
import { captureError, identifyUser, trackEvent } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type { AuthContextType, SignInData, SignUpData } from './types'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for changes on auth state (sign in, sign out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const newUser = session?.user ?? null
      setUser(newUser)

      // Track authentication events
      if (event === 'SIGNED_IN' && newUser) {
        identifyUser(newUser.id, {
          email: newUser.email,
          created_at: newUser.created_at,
        })
        trackEvent('feature_used', {
          feature: 'auth_sign_in',
          context: event === 'SIGNED_IN' ? 'auth_flow' : 'google',
        })
      } else if (event === 'SIGNED_OUT') {
        trackEvent('feature_used', {
          feature: 'auth_sign_out',
        })
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      if (error) throw error

      trackEvent('feature_used', {
        feature: 'auth_sign_in',
        context: 'google',
      })
    } catch (error) {
      captureError(error as Error, { context: 'signInWithGoogle' })
      throw error
    }
  }

  const signInWithEmail = async ({ email, password }: SignInData) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error

      trackEvent('feature_used', {
        feature: 'auth_sign_in',
        context: 'email',
      })
    } catch (error) {
      captureError(error as Error, { context: 'signInWithEmail' })
      throw error
    }
  }

  const signUpWithEmail = async ({ email, password, fullName }: SignUpData) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      })
      if (error) throw error

      trackEvent('feature_used', {
        feature: 'auth_sign_up',
        context: 'email',
      })
    } catch (error) {
      captureError(error as Error, { context: 'signUpWithEmail' })
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?from=email`,
      })
      if (error) throw error

      trackEvent('feature_used', {
        feature: 'auth_reset_password',
        context: 'email',
      })
    } catch (error) {
      captureError(error as Error, { context: 'resetPassword' })
      throw error
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      captureError(error as Error, { context: 'signOut' })
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
